import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import history from 'app/routes/history';
import _ from 'lodash';
import {
  IPaymentEventsSelector,
  IForfeitsSelector,
  ITeamsSelector,
  IGroupsSelector,
} from 'app/types/selector';
import SectionGroup from 'app/components/general/SectionGroup';
import SchemaForm from 'app/components/general/SchemaForm';
import PaymentCardMin from 'app/components/matches/PaymentCardMin';

import * as paymentEventsActions from 'app/ducks/paymentEvents/actions';
import * as forfeitsActions from 'app/ducks/forfeits/actions';
import * as groupsActions from 'app/ducks/groups/actions';

import * as loadingSelectors from 'app/ducks/loading/selectors';
import * as paymentEventsSelectors from 'app/ducks/paymentEvents/selectors';
import * as forfeitsSelectors from 'app/ducks/forfeits/selectors';
import * as organisationsSelectors from 'app/ducks/organisations/selectors';
import * as groupsSelectors from 'app/ducks/groups/selectors';
import * as teamsSelectors from 'app/ducks/teams/selectors';

function selector(state): any {
  return {
    appReady: loadingSelectors.appReady(state),
    paymentEvents: paymentEventsSelectors.getPaymentEvents(state),
    forfeits: forfeitsSelectors.getForfeits(state),
    organisations: organisationsSelectors.getOrganisations(state),
    groups: groupsSelectors.getGroups(state),
    teams: teamsSelectors.getTeams(state),
    isLoading: state.forfeits.isLoading,
    isSaving: state.forfeits.isSaving,
    hasSaved: state.forfeits.hasSaved,
    hasFailed: state.forfeits.hasFailed,
    errors: { ...state.paymentEvents.backendErrors },
  };
}

const actionCreators = _.merge(paymentEventsActions, forfeitsActions, groupsActions);

interface ISubmitForfeitProps {
  paymentEvents: IPaymentEventsSelector;
  forfeits: IForfeitsSelector;
  teams: ITeamsSelector;
  groups: any;
  match: any;
  actions: any;
  errors?: any;
  isLoading?: boolean;
  isSaving?: boolean;
  hasSaved?: boolean;
  hasFailed?: boolean;
}

interface ISubmitForfeitState {
  formData: any;
  originalFormData: any;
  changed: boolean;
  saved: boolean;
  errors: any;
}

const formSchema = {
  type: 'object',
  required: ['reason'],
  properties: {
    reason: {
      type: 'string',
      title: 'Reason for forfeit',
      inputType: 'textarea',
    },
  },
};

const uiSchema = {
  reason: { 'ui:field': 'textarea' },
};

@(connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
})) as any)
export default class SubmitForfeit extends React.Component<
ISubmitForfeitProps,
ISubmitForfeitState
> {
  static defaultProps: ISubmitForfeitProps = {
    paymentEvents: {},
    forfeits: {},
    teams: {},
    match: undefined,
    actions: undefined,
  };

  state = {
    changed: false,
    saved: false,
    formData: {},
    originalFormData: {},
    errors: {},
  };

  // static getDerivedStateFromProps(nextProps, prevState): any {
  //   if (!prevState.changed) {
  //     const formData = {
  //       bankName: nextProps.currentBankAccount.bankName || '',
  //       routingNumber: nextProps.currentBankAccount.routingNumber || '',
  //       accountNumber: nextProps.currentBankAccount.accountNumber || '',
  //       accountHolderName: nextProps.currentBankAccount.accountHolderName || '',
  //       country:
  //         nextProps.currentBankAccount && nextProps.currentBankAccount.country
  //           ? nextProps.currentBankAccount.country.id
  //           : 'AUS',
  //     };
  //     return {
  //       ...prevState,
  //       formData,
  //       originalFormData: formData,
  //       errors: nextProps.errors,
  //     };
  //   }
  //   return null;
  // }

  componentDidMount = () => {
    if (this.props.match.params.paymentEventId) {
      this.props.actions.getPaymentEvent(
        this.props.match.params.paymentEventId,
      );
      this.props.actions.selectPaymentEvent(
        this.props.match.params.paymentEventId,
      );
    }
    this.props.actions.getGroup(this.props.match.params.groupId);
  };

  isValid = (): boolean => _.isEmpty(this.state.errors) && this.state.changed;

  handleChange = (e): void => {
    const changed = this.state.originalFormData !== e.formData;
    this.setState(() => ({
      formData: e.formData,
      errors: [],
      changed,
    }));
  };

  handleError = (errors): void => {
    this.setState({ errors });
  }

  handleSubmit = () => {
    const { currentPaymentEvent } = this.props.paymentEvents;
    const { currentTeam } = this.props.teams;
    const { currentGroup } = this.props.groups;
    if (!_.isEmpty(currentPaymentEvent) && !_.isEmpty(currentGroup) && currentGroup?.id === this.props.match.params.groupId && currentPaymentEvent?.id === this.props.match.params.paymentEventId) {
      const otherGroupId = currentGroup.id === currentPaymentEvent.groupOne.id
        ? currentPaymentEvent.groupTwo.id
        : currentPaymentEvent.groupOne.id;
      const forfeitPayload = {
        reason: this.state.formData.reason,
        matchTime: currentPaymentEvent.eventAt,
        teamName: currentTeam?.group?.name,
        sectionName: currentPaymentEvent.sectionName,
        seasonName: currentPaymentEvent.seasonName,
        covid: true,
        group: {
          id: this.props.match.params.groupId,
        },
        competition: {
          id: currentGroup?.competition?.id,
        },
        oppositionGroup: {
          id: otherGroupId,
        },
        team: {
          id: currentTeam?.id,
        },
        fixtureMatch: {
          id: currentPaymentEvent?.id,
        },
        organisation: {
          id: currentPaymentEvent?.organisation?.id,
        },
      };
      this.props.actions.createForfeit(forfeitPayload);
    }
  };

  render(): React.ReactNode {
    const { currentPaymentEvent } = this.props.paymentEvents;
    if (!_.isEmpty(currentPaymentEvent)) {
      return (
        <div>
          <div className="row with-margin-bottom">
            <div className="col col-12 col-lg-10 col-xl-8">
              <div className="form-description">
                If your team is unable to play due to COVID, please lodge and pay for your
                forfeit here. Upon payment of this forfeit, the match will be
                cancelled and your organisation and the opposition team will be notified.
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col col-12 col-lg-10 col-xl-8">
              <SectionGroup title="Selected Match">
                {!_.isEmpty(currentPaymentEvent) && (
                  <div className="row">
                    <div className="col">
                      <PaymentCardMin
                        {...paymentEventsSelectors.getPaymentEventForMatchCard(
                          currentPaymentEvent,
                          this.props.match.params.groupId,
                        )}
                        truncated
                      />
                    </div>
                  </div>
                )}
              </SectionGroup>
            </div>
          </div>

          <div className="row with-margin-top">
            <div className="col col-12 col-lg-10 col-xl-8">
              <SchemaForm
                schema={formSchema}
                uiSchema={uiSchema}
                buttonText="Continue to payment"
                handleChange={this.handleChange}
                handleError={this.handleError}
                handleSubmit={this.handleSubmit}
                formData={this.state.formData}
                isValid={this.isValid()}
                isLoading={this.props.isSaving}
                hasSaved={this.props.hasSaved}
                errors={this.props.forfeits.backendErrors}
                hasFailed={this.props.hasFailed}
                originalFormData={this.state.originalFormData}
              />
            </div>
          </div>
        </div>
      );
    }
    return null;
  }
}
